/**
 * Product URL slug helpers (SFCC-11163).
 *
 * Produces SFRA-style URLs `/{slug}/{productId}.html` so PLP tile links,
 * PDP canonical/og:url, cross-sell tiles, variant URL rewrites, and sitemap
 * entries all share one slug source. The catch-all dispatcher resolves both
 * the slugged and bare forms identically — adding the slug is non-breaking.
 *
 * Slug rule (matches the shape CIO serves to SFRA):
 *   - lowercase
 *   - diacritics stripped (NFD + combining-mark removal)
 *   - any run of non-alphanumeric characters collapsed to a single `-`
 *   - no leading / trailing `-`
 */

// eslint-disable-next-line no-misleading-character-class
const COMBINING_MARKS = /[̀-ͯ]/g;
const NON_ALPHANUM = /[^a-z0-9]+/g;
const TRIM_DASHES = /^-+|-+$/g;

export function slugifyProductName(name: string | undefined | null): string {
  if (!name) return "";
  return name
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .toLowerCase()
    .replace(NON_ALPHANUM, "-")
    .replace(TRIM_DASHES, "");
}

/**
 * Build a product URL with the slug segment prefixed when a name is supplied.
 * Falls back to bare `/{id}.html` when the name is missing or slugifies to an
 * empty string — the dispatcher still resolves the bare form.
 */
export function buildProductUrl(id: string, name?: string | null): string {
  const slug = slugifyProductName(name);
  return slug ? `/${slug}/${id}.html` : `/${id}.html`;
}
